<template>
  <q-page :padding="!pairsList.length && $q.screen.gt.xs" class="docs-table" :style-fn="myTweak">
    <q-table
        :title="$t('pairs.menu.list')"
        ref="table"
        :data="serverData"
        :columns="columns"
        :filter="filter"
        row-key="id"
        :pagination.sync="serverPagination"
        :visible-columns="visibleColumns"
        :loading="loading"
        :selection="selection"
        :selected.sync="selectedRows"
        :rows-per-page-options="[10, 15, 30, 50, 100]"
        style="margin-bottom:80px"
        @request="all"
    >
      <template v-slot:top-selection="props" v-if="$auth.can('pairs.edit')">
        <q-btn color="negative" outline icon="fa fa-trash" :label="$t('delete')" class="q-mr-sm" @click="deleteRows"/>
        <q-btn color="secondary" outline icon="fa fa-edit" :label="$t('edit')" class="q-mr-sm"
               v-if="selectedRows.length === 1" @click="edit(selectedRows[0])"/>
        <q-btn color="pink-5" outline icon="fa fa-times" class="q-mr-sm" @click="selectedRows = []"/>
      </template>

      <template v-slot:top-right="props">
        <q-btn
            outline
            :label="$t('pairs.menu.create')"
            icon="fa fa-plus"
            color="positive"
            class="q-mr-sm"
            v-if="$auth.can('pairs.create')"
            @click="$router.push({name: 'pairs.create'})"
        />
      	<q-btn outline :label="$t('refresh')" icon="fas fa-sync" color="input-color" class="q-mr-sm" @click="all({pagination: serverPagination, filter})" :loading="loading">
			<template v-slot:loading>
		    <q-spinner-dots />
		  </template>
		</q-btn>
        <q-input dense debounce="300" v-model="filter" :placeholder="$t('search')" class="q-mr-sm">
          <template v-slot:prepend>
            <q-icon name="search" />
          </template>
        </q-input>
        <q-select
            v-model="visibleColumns"
            multiple
            dense
            options-dense
            :display-value="$q.lang.table.columns"
            emit-value
            map-options
            :options="columns"
            option-value="name"
            color="input-color"
            style="min-width: 150px; margin-right:10px"
        />
      </template>

      <!-- START CUSTOM COLUMNS -->
      <q-td slot="body-cell-exchange" slot-scope="props" :props="props">
          {{props.row.exchange ? props.row.exchange.name : '-'}}
      </q-td>
      <q-td slot="body-cell-bid_rate" slot-scope="props" :props="props" :class="getRatesClass(props.row.id, 1)">
          <div>
              <q-icon name="fa fa-exclamation-triangle" color="orange" text-color="white" v-if="!props.row.is_price_updated"/>&nbsp;
              <currency-format :value="props.row.bid_rate" style="display: inline-block"/>
          </div>
          <q-popup-edit v-model="props.row.bid_rate" buttons
                        @save="(val, initialValue) => editInline(val, initialValue, props.row, 'bid_rate')">
              <q-input v-model="props.row.bid_rate" dense autofocus type="number"/>
          </q-popup-edit>
      </q-td>
      <q-td slot="body-cell-ask_rate" slot-scope="props" :props="props" :class="getRatesClass(props.row.id, 0)">
          <div>
              <q-icon name="fa fa-exclamation-triangle" color="orange" text-color="white" v-if="!props.row.is_price_updated"/>&nbsp;
              <currency-format :value="props.row.ask_rate" style="display: inline-block"/>
          </div>
          <q-popup-edit v-model="props.row.ask_rate" buttons
                        @save="(val, initialValue) => editInline(val, initialValue, props.row, 'ask_rate')">
              <q-input v-model="props.row.ask_rate" dense autofocus type="number"/>
          </q-popup-edit>
      </q-td>
      <q-td slot="body-cell-slippage_percent" slot-scope="props" :props="props">
          <currency-format
              :value="props.row.slippage_percent"
              hide-white-spaces
              unit="%"
              style="display: inline-block"
          />
          <q-popup-edit v-model="props.row.slippage_percent" buttons
                        @save="(val, initialValue) => editInline(val, initialValue, props.row, 'slippage_percent')">
              <q-input v-model="props.row.slippage_percent" dense autofocus type="number"/>
          </q-popup-edit>
      </q-td>
      <q-td slot="body-cell-slippage_amount" slot-scope="props" :props="props">
          <currency-format :value="props.row.slippage_amount" style="display: inline-block"/>
          <q-popup-edit v-model="props.row.slippage_amount" buttons
                        @save="(val, initialValue) => editInline(val, initialValue, props.row, 'slippage_amount')">
              <q-input v-model="props.row.slippage_amount" dense autofocus type="number"/>
          </q-popup-edit>
      </q-td>
      <q-td slot="body-cell-is_api_enabled" slot-scope="props" :props="props" >
          <q-toggle
              v-model="props.row.is_api_enabled"
              checked-icon="fa fa-check"
              color="green"
              @input="editInline($event, props.row.is_api_enabled, props.row, 'is_api_enabled')"
              unchecked-icon="fa fa-times"
              :disable="loading || ! props.row.has_rich_exchange"
          />
      </q-td>
      <q-td slot="body-cell-is_rate_ok" slot-scope="props" :props="props" >
          <q-icon name="fa fa-flag" color="positive" v-if="props.row.is_rate_ok" />
          <q-icon name="fa fa-flag" color="negative" v-else />
      </q-td>
      <q-td slot="body-cell-updated_at" slot-scope="props" :props="props" >
          {{props.row.updated_at}}
      </q-td>
      <q-td slot="body-cell-setting" slot-scope="props" :props="props" v-if="$auth.can('pairs.edit')">
        <q-btn round size="sm" color="orange" icon="fa fa-edit" @click="edit(props.row)"/>&nbsp;
        <q-btn round size="sm" color="red" icon="fa fa-trash" @click="deleteRow(props.row)"/>
      </q-td>
      <!-- END CUSTOM COLUMNS -->
    </q-table>
  </q-page>
</template>

<script>
  import Pair from '../models/Pair.js'
  export default {
    name: 'PairList',
    props: {
        pairsList: {
            type: Array,
            default: () => ([])
        }
    },
    computed: {
	  columns() {
        let columns = [
          {name: 'id', required: true, label: this.$t('pairs.table_titles.id'), align: 'left', field: 'id', sortable: true},
          {name: 'symbol', label: this.$t('pairs.table_titles.symbol'), align: 'left', field: 'symbol', sortable: true},
          {name: 'exchange', label: this.$t('pairs.table_titles.exchange'), align: 'left', field: 'exchange', sortable: false},
          {name: 'bid_rate', label: this.$t('pairs.table_titles.bid_rate'), align: 'left', field: 'bid_rate', sortable: true},
          {name: 'ask_rate', label: this.$t('pairs.table_titles.ask_rate'), align: 'left', field: 'ask_rate', sortable: true},
          {name: 'slippage_percent', label: this.$t('pairs.table_titles.slippage_percent'), align: 'left', field: 'slippage_percent', sortable: true},
          {name: 'slippage_amount', label: this.$t('pairs.table_titles.slippage_amount'), align: 'left', field: 'slippage_amount', sortable: true},
          {name: 'is_api_enabled', label: this.$t('pairs.table_titles.is_api_enabled'), align: 'center', field: 'is_api_enabled', sortable: false},
          {name: 'is_rate_ok', label: this.$t('pairs.table_titles.is_rate_ok'), align: 'center', field: 'is_rate_ok', sortable: false},
          {name: 'updated_at', label: this.$t('pairs.table_titles.updated_at'), align: 'center', field: 'updated_at', sortable: false},
        ]

        // add Edit/Delete column just if the user has appropriate permission
        if(this.$auth.can('pairs.edit')){
          this.selection = 'multiple'
          this.visibleColumns.push('setting')
          columns.push({name: 'setting', align: 'center', label: this.$t('pairs.table_titles.setting'), sortable: false, field: 'setting'})
        }
        return columns;
      },
    },
    data: () => ({
      separator: 'horizontal',
      filter: '',
      loading: false,
      serverPagination: {
        page: 1,
        sortBy: 'id',
        rowsPerPage: 15,
        descending: false,
        rowsNumber: 10 // specifying this determines pagination is server-side
      },
      visibleColumns: ['id','symbol', 'exchange', 'bid_rate', 'ask_rate', 'is_api_enabled', 'updated_at'],
      serverData: [],
      selection: 'none',
      selectedRows: []
    }),
    methods: {
      all({pagination, filter}) {
        this.loading = true
        Pair.all(`sort=${pagination.sortBy}%7C${pagination.descending ? 'desc' : 'asc'}&page=${pagination.page}&per_page=${pagination.rowsPerPage}&filter=${filter}&pairs=${this.pairsList.join(',')}`, ({data, meta}) => {
          this.serverPagination = pagination
          // we also set (or update) rowsNumber
          this.serverPagination.rowsNumber = meta.total
          this.serverData = data
          this.loading = false
        }).catch(error => {
          this.$q.notify({
            message: error.data.error,
            color: 'negative',
            icon: 'fa fa-exclamation-triangle'
          })
          this.loading = false
        })
      },
      editInline(val, initVal, row, col) {
        row[col] = val
        this.loading = true
        new Pair({
            id: row.id,
            symbol: row.symbol,
            exchange_id: row.exchange ? row.exchange.id : null,
            bid_rate: col === 'bid_rate' ? val : row.bid_rate,
            ask_rate: col === 'ask_rate' ? val : row.ask_rate,
            slippage_amount: col === 'slippage_amount' ? val : row.slippage_amount,
            slippage_percent: col === 'slippage_percent' ? val : row.slippage_percent,
            is_api_enabled: col === 'is_api_enabled' ? (val ? 1 : 0) : row.is_api_enabled,
        }).update((data) => {
            for (let field in data) {
                row[field] = data[field]
            }
            this.loading = false
        }).catch(({data}) => {
            this.dialog = true
            this.errorTitle = data.error
            this.errorMessages = []
            for (let err in data.errors) {
                this.errorMessages.push(data.errors[err][0])
            }
            row[col] = initVal
            this.loading = false
        })
      },
      edit(value) {
        this.$router.push({name: 'pairs.edit', params: {id: value.id}})
      },
      deleteRow(row) {
        this.selectedRows.push(row);
        this.deleteRows();
      },
      deleteRows() {
        this.$q.dialog({
          title: 'Delete information',
          message: 'Are your sure?',
          ok: 'Yes',
          cancel: 'No'
        }).onOk(() => {
          this.$q.loading.show()
          this.selectedRows.map((row) => {
            let pair = new Pair({id: row.id});
            pair.destroy(() => {
              this.serverData = this.serverData.filter(record => record.id !== row.id);
              this.selectedRows = this.selectedRows.filter(member => member.id !== row.id);
              if(!this.selectedRows.length){
                this.$q.loading.hide()
              }
            }).catch(({data}) => {
              this.$q.notify(data.error)
              this.$q.loading.hide()
            })
          });
        }).onCancel(() => {
          this.$q.loading.hide()
        })
      },
      myTweak (offset) {
          return { minHeight: offset ? `calc(35vh - ${offset}px)` : '35vh' }
      },
      getRatesClass(id, baseIndex) {
        if (!this.pairsList.length) return ''
        return this.pairsList[baseIndex] === id ? 'text-bold text-input-color' : 'text-grey'
      }
    },
    beforeCreate() {
      if(! this.$auth.can('pairs.list')){
        this.$router.push({name: 'dashboard'})
      }
    },
    mounted() {
      // once mounted, we need to trigger the initial server data fetch
        if(this.$router.currentRoute.query.q !== undefined) {
            this.filter = this.$router.currentRoute.query.q
        } else {
            this.all({
                pagination: this.serverPagination,
                filter: this.filter
            })
        }
    },
    components: {}
  }
</script>
